import env from 'configs/env';
import { computed } from 'vue';

export function useSeoManager() {

    /**
     * Generates a JSON-LD object for a job posting to conform with Google's structured data guidelines.
     * The method ensures the representation of job information is accurate, comprehensive, and well-structured
     * for search engine optimization. Includes details such as employment type, location, hiring organization,
     * salary, and more.
     *
     * @param {Object} job - A reactive object containing job details, where job.value holds the core data for processing.
     * @param {string} canonicalUrl - The canonical URL of the job posting or the base client URL if not explicitly provided.
     * @return {Object|null} - A JSON-LD object representing the job posting or null if the job data is incomplete or invalid.
     */
    function generateJobJsonLd(job, canonicalUrl) {
        return computed(() => {
            if (!job.value) {
                return null;
            }
            const jobData = job.value;
            // If job is paused or expired, don't generate JSON+LD
            if ((!jobData.owner && jobData?.status === 'Paused') ||
                (!jobData.owner && jobData?.status === 'Expired')) {
                return null;
            }
            const activeScope = jobData.scopes?.[0]; // Use first scope as primary location
            if (!activeScope) {
                return null;
            }
            const fullUrl = canonicalUrl || `${env.clientBaseURL}`;
            // Map employment type from your format to Google's expected format
            const employmentTypeMap = {
                'Full-time': 'FULL_TIME',
                'Part-time': 'PART_TIME',
                'Contract': 'CONTRACTOR',
                'Temporary': 'TEMPORARY',
                'Internship': 'INTERN',
                'Voluntary': 'VOLUNTEER',
                'Casual': 'OTHER',
                'Other': 'OTHER'
            };
            // Map remuneration cycle to Google's expected format
            const remunerationCycleMap = {
                'Hour': 'HOUR',
                'Day': 'DAY',
                'Month': 'MONTH',
                'Year': 'YEAR'
            };
            // Get an appropriate description: prefer summary first (which is already limited to 256 chars)
            // or truncate the plaintext description to 500 chars
            let description = '';
            if (jobData.summary) {
                description = jobData.summary;
            } else if (jobData.descriptionPlaintext) {
                description = truncateText(jobData.descriptionPlaintext, 500);
            }
            const jsonLd = {
                '@context': 'https://schema.org/',
                '@type': 'JobPosting',
                'title': jobData.title,
                'description': description,
                'datePosted': jobData.publishedAt,
                'validThrough': jobData.expiresAt,
                'employmentType': employmentTypeMap[jobData.employmentType] || 'FULL_TIME',
                'hiringOrganization': {
                    '@type': 'Organization',
                    'name': jobData.company.name,
                    'sameAs': jobData.company.websiteUrl || fullUrl,
                    'logo': jobData.company.logoUrl
                },
                'jobLocation': {
                    '@type': 'Place',
                    'address': {
                        '@type': 'PostalAddress',
                        'addressLocality': activeScope.location?.name || '',
                        'addressRegion': activeScope.location?.region || '',
                        'addressCountry': activeScope.location?.isoCode || ''
                    }
                },
                'url': fullUrl,
                'identifier': {
                    '@type': 'PropertyValue',
                    'name': 'Xprt Jobs',
                    'value': jobData.id.toString()
                }
            };
            // Add industry from classification if available
            if (jobData.classification && jobData.classification.name) {
                jsonLd.industry = jobData.classification.name;
                // If there's a parent classification, add it to create a more complete industry description
                if (jobData.parentClassification && jobData.parentClassification.name) {
                    jsonLd.industry = `${jobData.parentClassification.name} - ${jsonLd.industry}`;
                }
            }
            // Add salary information if available
            if (activeScope.remuneration && activeScope.remuneration.range &&
                activeScope.remuneration.range[0] && activeScope.remuneration.range[1]) {
                jsonLd.baseSalary = {
                    '@type': 'MonetaryAmount',
                    'currency': activeScope.location?.currencyCode || 'USD',
                    'value': {
                        '@type': 'QuantitativeValue',
                        'minValue': activeScope.remuneration.range[0],
                        'maxValue': activeScope.remuneration.range[1],
                        'unitText': remunerationCycleMap[activeScope.remuneration.cycle] || 'YEAR'
                    }
                };
            }
            // Google only supports "TELECOMMUTE" for Remote jobs and no value for On-site roles. As there is no explicit
            // value for Hybrid roles, it's safest just to omit the value and let Google assume it's On-site.
            if (activeScope.workLocation === 'Remote') {
                jsonLd.jobLocationType = 'TELECOMMUTE';
                // Default to Australia if no specific location is associated
                console.log(activeScope.location);
                const countryCode = activeScope.location?.isoCode || 'AU';
                jsonLd.applicantLocationRequirements = {
                    '@type': 'Country',
                    'name': countryCode
                };
            }
            return jsonLd;
        });
    }

    /**
     * Truncates a given text to a specified maximum length, ensuring that words are not cut in half. If the text is
     * truncated, an ellipsis ('...') is appended. If the provided text is null, undefined, or shorter than the specified
     * maximum length, it is returned as-is without modification.
     *
     * @param {string} text - The input text to be truncated.
     * @param {number} [maxLength=500] - The maximum length of the truncated text, including the ellipsis. Defaults to 500.
     * @return {string} - The truncated version of the input text, or the original text if no truncation is necessary.
     */
    function truncateText(text, maxLength = 500) {
        if (!text || text.length <= maxLength) {
            return text || '';
        }
        // Cut at maxLength and then find the last space to avoid cutting words
        const truncated = text.substring(0, maxLength);
        const lastSpaceIndex = truncated.lastIndexOf(' ');
        if (lastSpaceIndex === -1) {
            return truncated; // No spaces found, just cut at maxLength
        }
        return truncated.substring(0, lastSpaceIndex) + '...';
    }

    return {
        generateJobJsonLd
    };
}
